import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import moment from 'moment';
import { useDataProvider } from 'react-admin';
import { daysOfTheWeek, frequencyChoices, scoringTypes } from '../../../constants';

const FormSummary: React.FC<{ formData?: any }> = ({ formData }) => {
  const dataProvider = useDataProvider();
  const [stationsMap, setStationsMap] = useState<Map<string, string>>(new Map());
  const [citiesMap, setCitiesMap] = useState<Map<string, string>>(new Map());
  const [tagsMap, setTagsMap] = useState<Map<string, string>>(new Map());

  // Buscar nomes dos postos
  useEffect(() => {
    const loadStationNames = async () => {
      if (!formData?.stationIds || formData.stationIds.length === 0) return;

      try {
        const { data } = await dataProvider.getMany('stations', {
          ids: formData.stationIds,
        });

        const map = new Map<string, string>();
        data.forEach((station: any) => {
          map.set(station.id, station.name);
        });
        setStationsMap(map);
      } catch (error) {
        console.error('Erro ao carregar nomes dos postos:', error);
      }
    };

    loadStationNames();
  }, [formData?.stationIds, dataProvider]);

  // Buscar nomes das cidades
  useEffect(() => {
    const loadCityNames = async () => {
      if (!formData?.cityIds || formData.cityIds.length === 0) return;

      try {
        const { data } = await dataProvider.getMany('cities', {
          ids: formData.cityIds,
        });

        const map = new Map<string, string>();
        data.forEach((city: any) => {
          map.set(city.id, city.name);
        });
        setCitiesMap(map);
      } catch (error) {
        console.error('Erro ao carregar nomes das cidades:', error);
      }
    };

    loadCityNames();
  }, [formData?.cityIds, dataProvider]);

  // Buscar nomes das tags
  useEffect(() => {
    const loadTagNames = async () => {
      if (!formData?.customerTagIds || formData.customerTagIds.length === 0) return;

      try {
        const { data } = await dataProvider.getMany('chains/chainId/tag', {
          ids: formData.customerTagIds,
        });

        const map = new Map<string, string>();
        data.forEach((tag: any) => {
          map.set(tag.id, tag.name);
        });
        setTagsMap(map);
      } catch (error) {
        console.error('Erro ao carregar nomes das tags:', error);
      }
    };

    loadTagNames();
  }, [formData?.customerTagIds, dataProvider]);

  const getScoringTypeInfo = (type: string) => {
    if (type === 'default') {
      return { label: 'Padrão', color: '#2e7d32', bgColor: '#e8f5e9' };
    }
    return { label: 'Especial', color: '#1976d2', bgColor: '#e3f2fd' };
  };

  const typeInfo = getScoringTypeInfo(formData?.type);

  return (
    <Box sx={{ p: 0, pt: 2 }}>
      <Typography variant="h5" gutterBottom fontWeight={700} mb={3}>
        Revisão Final da Pontuação
      </Typography>

      <Grid container spacing={2}>
        {/* Seção de Informações Básicas */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <StarIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>Informações Básicas</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">Tipo de Pontuação</Typography>
                <Box mt={0.5}>
                  <Chip
                    label={typeInfo.label}
                    sx={{
                      bgcolor: typeInfo.bgColor,
                      color: typeInfo.color,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Grid>
              {formData?.type === 'special' && formData?.description && (
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Descrição</Typography>
                  <Typography variant="body1" fontWeight={600}>{formData.description}</Typography>
                </Grid>
              )}
              {formData?.type === 'special' && (
                <>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Limite de uso</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.usageLimit || 'Ilimitado'}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Intervalo (horas)</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.intervalInHours || '--'}
                    </Typography>
                  </Grid>
                  {formData.overlapsStandardScoring && (
                    <Grid size={12}>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Sobrepõe pontuação padrão"
                        color="warning"
                        size="small"
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Seção de Postos */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <LocalGasStationIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>Postos</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {formData?.selectsAllPlaces ? (
              <Chip label="Todos os postos" color="success" icon={<CheckCircleIcon />} />
            ) : formData?.stationIds?.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.stationIds.map((stationId: string, index: number) => (
                  <Chip
                    key={index}
                    label={stationsMap.get(stationId) || stationId}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            ) : (
              <Alert severity="warning">Nenhum posto selecionado</Alert>
            )}
          </Paper>
        </Grid>

        {/* Seção de Pontuação por Combustível */}
        <Grid size={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <LocalGasStationIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>Pontuação por Combustível</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {formData?.fuelScorings && formData.fuelScorings.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Combustível</strong></TableCell>
                      <TableCell align="right"><strong>Fator (pts/litro)</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.fuelScorings
                      .filter((fs: any) => !formData.availableFuelIds || formData.availableFuelIds.includes(fs.id))
                      .map((fuelScoring: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{fuelScoring.name}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={fuelScoring.factor || 0}
                              size="small"
                              color={fuelScoring.factor > 0 ? 'success' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="warning">Nenhum combustível configurado</Alert>
            )}
          </Paper>
        </Grid>

        {/* Seção de Cidades (apenas para pontuação especial) */}
        {formData?.type === 'special' && formData?.cityIds?.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <LocationOnIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>Cidades</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.cityIds.map((cityId: string, index: number) => (
                  <Chip
                    key={index}
                    label={citiesMap.get(cityId) || cityId}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Seção de Etiquetas de Clientes */}
        {formData?.type === 'special' && (formData?.customerTagIds?.length > 0 || formData?.customerIds?.length > 0) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <PeopleIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>Segmentação de Clientes</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {formData?.customerTagIds?.length > 0 && (
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Etiquetas de clientes:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {formData.customerTagIds.map((tagId: string, index: number) => (
                      <Chip
                        key={index}
                        label={tagsMap.get(tagId) || tagId}
                        color="secondary"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {formData?.customerIds?.length > 0 && (
                <Chip
                  label={`${formData.customerIds.length} clientes selecionados`}
                  color="info"
                  icon={<PeopleIcon />}
                />
              )}
            </Paper>
          </Grid>
        )}

        {/* Seção de Agendamento */}
        {formData?.type === 'special' && formData?.isSchedule && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <ScheduleIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>Agendamento</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Data inicial</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formData.startAt ? moment(formData.startAt).format('DD/MM/YYYY HH:mm') : '--'}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Data final</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formData.endAt ? moment(formData.endAt).format('DD/MM/YYYY HH:mm') : '--'}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Frequência</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {frequencyChoices.find(f => f.id === formData.frequency)?.name || '--'}
                  </Typography>
                </Grid>
              </Grid>

              {formData.frequency === 'weekly' && formData.weekSchedual && (
                <Box mt={2}>
                  <Typography variant="subtitle2" mb={1}>Horários Semanais</Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>Dia</strong></TableCell>
                          <TableCell align="center"><strong>Horário</strong></TableCell>
                          <TableCell align="center"><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.weekSchedual.map((schedule: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              {daysOfTheWeek.find(d => d.id === schedule.dayOfTheWeek)?.name}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem' }}>
                              {schedule.startTime && schedule.endTime
                                ? `${moment(schedule.startTime).format('HH:mm')} - ${moment(schedule.endTime).format('HH:mm')}`
                                : '--'
                              }
                            </TableCell>
                            <TableCell align="center">
                              {schedule.isActive ? (
                                <Chip icon={<CheckCircleIcon />} label="Ativo" size="small" color="success" />
                              ) : (
                                <Chip icon={<CancelIcon />} label="Inativo" size="small" color="default" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {formData.frequency === 'daily' && (
                <Grid container spacing={2} mt={1}>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Hora de início</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.startTime ? moment(formData.startTime).format('HH:mm') : '--'}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Hora de fim</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.endTime ? moment(formData.endTime).format('HH:mm') : '--'}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
        )}

        {/* Seção de Notificação */}
        {formData?.hasNotification && formData?.notification && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <NotificationsActiveIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>Notificação</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {formData.notification.title && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">Título</Typography>
                    <Typography variant="body1" fontWeight={600}>{formData.notification.title}</Typography>
                  </Grid>
                )}
                {formData.notification.body && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">Mensagem</Typography>
                    <Box
                      dangerouslySetInnerHTML={{ __html: formData.notification.body }}
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        '& img': { maxWidth: '100%' }
                      }}
                    />
                  </Grid>
                )}
                <Grid size={12}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {formData.notification.pushNotification && (
                      <Chip label="Push" size="small" color="info" />
                    )}
                    {formData.notification.sms && (
                      <Chip label="SMS" size="small" color="secondary" />
                    )}
                  </Stack>
                </Grid>
                {formData.notification.pushNotification && formData.notification.pushMessage && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">Mensagem Push</Typography>
                    <Typography variant="body2">{formData.notification.pushMessage}</Typography>
                  </Grid>
                )}
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Envio</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formData.notification.sendNow
                      ? 'Imediatamente'
                      : formData.notification.sendAt
                        ? moment(formData.notification.sendAt).format('DD/MM/YYYY [às] HH:mm')
                        : 'Imediatamente'
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>Atenção:</strong> Revise todas as informações antes de confirmar a criação da pontuação.
      </Alert>
    </Box>
  );
};

export default FormSummary;
